import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  Calendar,
  Mail,
  Eye,
  Loader2,
  Crown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MoreVertical,
  Globe,
  Phone,
  Briefcase,
  UserCog,
  Pause,
  Play,
  DollarSign,
  Shield,
  Settings,
  Save,
  X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ADMIN_BASE_URL } from '../config/admin';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MSPDetail() {
  const { mspId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [msp, setMsp] = useState(null);
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit form
  const [editForm, setEditForm] = useState({
    company_name: '',
    website: '',
    phone: '',
    status: 'active',
  });
  
  // Dialog states
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [showEditTeamDialog, setShowEditTeamDialog] = useState(false);
  const [showDeleteTeamDialog, setShowDeleteTeamDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Team edit form
  const [teamEditForm, setTeamEditForm] = useState({
    name: '',
    role: 'admin',
    is_active: true,
  });

  useEffect(() => {
    fetchMspDetails();
  }, [mspId]);

  const fetchMspDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [mspRes, teamRes, clientsRes] = await Promise.all([
        axios.get(`${API}/api/admin/msps/${mspId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/msps/${mspId}/team`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/msps/${mspId}/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);
      
      setMsp(mspRes.data.msp);
      setTeam(teamRes.data.team || []);
      setClients(clientsRes.data.clients || []);
      
      // Initialize edit form
      setEditForm({
        company_name: mspRes.data.msp.company_name || '',
        website: mspRes.data.msp.website || '',
        phone: mspRes.data.msp.phone || '',
        status: mspRes.data.msp.status || 'active',
      });
    } catch (error) {
      toast.error('Failed to fetch MSP details');
      navigate(`${ADMIN_BASE_URL}/msps`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMsp = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/msps/${mspId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MSP updated successfully');
      setIsEditing(false);
      fetchMspDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MSP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuspendMsp = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const isSuspended = msp.status !== 'suspended';
      await axios.put(`${API}/api/admin/msps/${mspId}/suspend`, 
        { is_suspended: isSuspended },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`MSP ${isSuspended ? 'suspended' : 'reactivated'} successfully`);
      setShowSuspendDialog(false);
      fetchMspDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update MSP status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMsp = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/msps/${mspId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MSP deleted successfully');
      navigate(`${ADMIN_BASE_URL}/msps`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete MSP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTeamMember = async () => {
    if (!selectedTeamMember) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/admin/msps/${mspId}/team/${selectedTeamMember.id}`, 
        teamEditForm,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Team member updated successfully');
      setShowEditTeamDialog(false);
      fetchMspDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTeamMember = async () => {
    if (!selectedTeamMember) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/admin/msps/${mspId}/team/${selectedTeamMember.id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Team member deleted successfully');
      setShowDeleteTeamDialog(false);
      fetchMspDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team member');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditTeamDialog = (member) => {
    setSelectedTeamMember(member);
    setTeamEditForm({
      name: member.name || '',
      role: member.role || 'admin',
      is_active: member.is_active !== false,
    });
    setShowEditTeamDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><AlertTriangle className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline" className="border-slate-600">{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30"><Crown className="w-3 h-3 mr-1" />Owner</Badge>;
      case 'admin':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Shield className="w-3 h-3 mr-1" />Admin</Badge>;
      case 'technician':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><Settings className="w-3 h-3 mr-1" />Technician</Badge>;
      default:
        return <Badge variant="outline" className="border-slate-600">{role}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!msp) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">MSP Not Found</h3>
        <Button onClick={() => navigate(`${ADMIN_BASE_URL}/msps`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to MSPs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`${ADMIN_BASE_URL}/msps`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl">
              {(msp.company_name || 'M')[0].toUpperCase()}
            </div>
            <div>
              {isEditing ? (
                <Input
                  value={editForm.company_name}
                  onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  className="text-xl font-bold bg-slate-800 border-slate-700"
                />
              ) : (
                <h1 className="text-2xl font-bold text-white">{msp.company_name}</h1>
              )}
              <p className="text-slate-400">{msp.contact_email}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSaveMsp} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button 
                variant="outline"
                className={msp.status === 'suspended' ? 'text-emerald-400' : 'text-amber-400'}
                onClick={() => setShowSuspendDialog(true)}
              >
                {msp.status === 'suspended' ? <Play className="w-4 h-4 mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                {msp.status === 'suspended' ? 'Reactivate' : 'Suspend'}
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* MSP Info Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 text-sm">Status</span>
              {isEditing ? (
                <Select 
                  value={editForm.status} 
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger className="w-[120px] h-8 bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                getStatusBadge(msp.status)
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-white">{clients.length}</p>
                <p className="text-xs text-slate-400">Client Companies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-white">{team.length}</p>
                <p className="text-xs text-slate-400">Team Members</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-lg font-bold text-white">
                  {new Date(msp.created_at).toLocaleDateString()}
                </p>
                <p className="text-xs text-slate-400">Member Since</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contact Info (when editing) */}
      {isEditing && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg text-white">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={editForm.website}
                onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                placeholder="https://example.com"
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Team and Clients */}
      <Tabs defaultValue="team" className="space-y-4">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="team" className="data-[state=active]:bg-slate-800">
            <Users className="w-4 h-4 mr-2" />
            Team ({team.length})
          </TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-slate-800">
            <Briefcase className="w-4 h-4 mr-2" />
            Clients ({clients.length})
          </TabsTrigger>
        </TabsList>

        {/* Team Tab */}
        <TabsContent value="team">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Role</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.map((member) => (
                    <TableRow key={member.id} className="border-slate-800">
                      <TableCell className="font-medium text-white">{member.name}</TableCell>
                      <TableCell className="text-slate-400">{member.email}</TableCell>
                      <TableCell>{getRoleBadge(member.role)}</TableCell>
                      <TableCell>
                        {member.is_active !== false ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(member.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                            <DropdownMenuItem 
                              className="cursor-pointer"
                              onClick={() => openEditTeamDialog(member)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem 
                              className="cursor-pointer text-red-400 focus:text-red-400"
                              onClick={() => {
                                setSelectedTeamMember(member);
                                setShowDeleteTeamDialog(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {team.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No team members found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients Tab */}
        <TabsContent value="clients">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400">Company</TableHead>
                    <TableHead className="text-slate-400">Plan</TableHead>
                    <TableHead className="text-slate-400">Users</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Created</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.id} className="border-slate-800">
                      <TableCell className="font-medium text-white">{client.company_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-600 capitalize">
                          {client.plan || 'trial'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-400">{client.user_count || 0}</TableCell>
                      <TableCell>
                        {client.status === 'active' ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                        ) : (
                          <Badge className="bg-slate-500/20 text-slate-400">{client.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-400">
                        {new Date(client.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`${ADMIN_BASE_URL}/companies/${client.id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {clients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                        No client companies found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Team Member Dialog */}
      <Dialog open={showEditTeamDialog} onOpenChange={setShowEditTeamDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Team Member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update team member information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={teamEditForm.name}
                onChange={(e) => setTeamEditForm({ ...teamEditForm, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select 
                value={teamEditForm.role} 
                onValueChange={(value) => setTeamEditForm({ ...teamEditForm, role: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={teamEditForm.is_active ? 'active' : 'inactive'} 
                onValueChange={(value) => setTeamEditForm({ ...teamEditForm, is_active: value === 'active' })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTeamDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTeamMember} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Member Dialog */}
      <Dialog open={showDeleteTeamDialog} onOpenChange={setShowDeleteTeamDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Team Member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>&quot;{selectedTeamMember?.name}&quot;</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteTeamDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTeamMember} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend MSP Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {msp.status === 'suspended' ? 'Reactivate' : 'Suspend'} MSP
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {msp.status === 'suspended' 
                ? `Are you sure you want to reactivate "${msp.company_name}"? This will restore access for all their team members.`
                : `Are you sure you want to suspend "${msp.company_name}"? This will prevent all their team members from accessing the platform.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSuspendMsp} 
              disabled={submitting}
              className={msp.status === 'suspended' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {msp.status === 'suspended' ? 'Reactivate' : 'Suspend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete MSP Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete MSP
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete <strong>&quot;{msp.company_name}&quot;</strong>?
              <br /><br />
              <span className="text-red-400 font-medium">This action cannot be undone.</span> This will permanently delete:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>The MSP account and all settings</li>
                <li>All {team.length} team members</li>
                <li>All {clients.length} client companies and their data</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteMsp} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete MSP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
